import React from 'react';
import styled from '@emotion/styled';
import { Color, FontSize, Radius } from 'views/constants';
import { Typography } from 'views/components/atoms/Typography';

type ButtonType = 'submit' | 'reset' | 'button';
type StyleType = 'primary' | 'default' | 'secondary';

type ButtonProps = {
  type: ButtonType;
  height?: string;
  fontSize?: FontSize;
  style?: StyleType;
  disabled?: boolean;
  onClick?(): void;
};

type StyledButtonProps = {
  style: {
    color: Color;
    backgroundColor: Color;
    borderColor: Color;
    height: string;
  };
};

const primaryStyle = {
  color: Color.C6,
  backgroundColor: Color.C7,
  borderColor: Color.C8,
  bold: true,
};

const defaultStyle = {
  color: Color.C1,
  backgroundColor: Color.C6,
  borderColor: Color.C13,
  bold: true,
};

const secondaryStyle = {
  color: Color.C2,
  backgroundColor: Color.C5,
  borderColor: Color.C14,
  bold: true,
};

const disabledStyle = {
  color: Color.C4,
  backgroundColor: Color.C5,
  borderColor: Color.C14,
  bold: true,
};

const StyledButton = styled.button(`
  color: ${({ style }: StyledButtonProps): string => style.color};
  background-color: ${({ style }: StyledButtonProps): string =>
    style.backgroundColor};
  border-color: ${({ style }: StyledButtonProps): string => style.borderColor};
  width: 100%;
  border-width: 1px;
  border-style: solid;
  border-radius: ${Radius.DEFAULT};
  height: ${({ style }: StyledButtonProps): string => style.height};
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
  &:disabled:hover {
    cursor: initial;
  }
`);

const styleMap: {
  [P in StyleType]: {
    color: Color;
    backgroundColor: Color;
    borderColor: Color;
    bold: boolean;
  };
} = {
  primary: primaryStyle,
  default: defaultStyle,
  secondary: secondaryStyle,
};

export const Button: React.FC<ButtonProps> = ({
  type,
  children,
  height = '44px',
  fontSize = FontSize.DEFAULT,
  style = 'default',
  disabled,
  onClick,
}) => {
  const _style = disabled ? disabledStyle : styleMap[style];

  return (
    <StyledButton
      type={type}
      disabled={disabled}
      style={{ ..._style, height }}
      onClick={onClick}
    >
      <Typography size={fontSize} color={_style.color} bold={_style.bold}>
        {children}
      </Typography>
    </StyledButton>
  );
};
